import './mailCodeActivationPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import trim from 'lodash/trim';
import { ReactNode, useEffect, useMemo } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import vehicleSUVTopView from '../../../assets/vehicle-suv-topview.png';
import { Header } from '../../../components/Header/Header';
import { CheckedIcon } from '../../../components/Icons/CheckedIcon';
import { UncheckedIcon } from '../../../components/Icons/UncheckedIcon';
import { LanderImage } from '../../../components/LanderImage/LanderImage';
import { PhoneNumberInput } from '../../../components/PhoneNumberInput/PhoneNumberInput';
import {
  emailValidator,
  nameValidator,
} from '../../../components/utils/inputValidators';
import useAnalyticStore from '../../../store/analyticStore/useAnalyticStore';
import useApiStore from '../../../store/apiStore/useApiStore';
import { smsOptInCheckboxVariantDefault } from '../../../store/featureStore/constants/variantDefaults';
import { FeatureFlag } from '../../../store/featureStore/enums/FeatureFlag';
import { useFeatureVariant } from '../../../store/featureStore/hooks/useFeatureVariant';
import useFeatureStore from '../../../store/featureStore/useFeatureStore';
import useLeadChannelStore from '../../../store/leadChannelStore/useLeadChannelStore';
import useMailCodeStore from '../../../store/mailCodeStore/useMailCodeStore';
import { DeliverPinLocation } from '../../../store/pinStore/enums/DeliverPinLocation';
import usePinStore from '../../../store/pinStore/usePinStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useSignUpStore from '../../../store/signUpStore/useSignUpStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigateWithCallback = useNavigateWithCallback();

  const { isLoading } = useApiStore();
  const {
    computed: { brand },
  } = useLeadChannelStore();

  const { t } = useTranslation();
  const { sendSignUpVariantAssignedAnalytics } = useAnalyticStore();

  const { deliverPin, deliverPinFailedMessage } = usePinStore();

  const {
    deriveDeliverPinParams,
    clearUserSuccessState,
    setSmsOptIn,
    userExists,
  } = useUserStore();

  const { isReady } = useFeatureStore();

  const smsOptInCheckboxVariant = useFeatureVariant(
    FeatureFlag.ENABLE_SMS_OPT_IN_CHECKBOX,
    smsOptInCheckboxVariantDefault,
  );

  const {
    computed: {
      activationSubtitleText,
      activationTitleText,
      applicantFirstName,
      applicantLastName,
    },
    firstName,
    lastName,
    primaryPhoneNumber,
    primaryEmailAddress,
    setMailCodeActivationState,
  } = useMailCodeStore();

  const {
    computed: { reviewsAsset, reviewsUrl },
  } = useSignUpStore();

  const form = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    defaultValues: {
      firstNameValue: firstName || applicantFirstName,
      lastNameValue: lastName || applicantLastName,
      primaryEmailAddressValue: primaryEmailAddress,
      primaryPhoneNumberValue: primaryPhoneNumber || '',
      smsOptInValue: false,
    },
    delayError: 200,
    shouldFocusError: false,
  });

  const {
    control,
    getValues,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const firstNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.firstName.required'),
    minLength: {
      value: 2,
      message: t('validation.firstName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.firstName.invalid'),
    },
  };

  const lastNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.lastName.required'),
    minLength: {
      value: 2,
      message: t('validation.lastName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.lastName.invalid'),
    },
  };

  const emailAddressValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.emailAddress.required'),
    pattern: {
      value: emailValidator,
      message: t('validation.emailAddress.invalid'),
    },
  };

  const smsOptInCheckboxClassName = useMemo(
    (): string =>
      [
        'checkbox-described',
        'checkbox-described-sm',
        watch('smsOptInValue') && 'checkbox-described-checked',
      ]
        .filter(Boolean)
        .join(' '),
    [watch('smsOptInValue')],
  );

  const handleSmsOptInCheckboxClicked = (): void => {
    setValue('smsOptInValue', !getValues('smsOptInValue'));
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      firstNameValue,
      lastNameValue,
      primaryPhoneNumberValue,
      primaryEmailAddressValue,
      smsOptInValue,
    } = values;

    await userExists(primaryPhoneNumberValue, primaryEmailAddressValue);

    const { userExistsException } = useUserStore.getState();

    setSmsOptIn(smsOptInValue);

    setMailCodeActivationState({
      firstNameValue,
      lastNameValue,
      primaryEmailAddressValue,
      primaryPhoneNumberValue,
    });

    const deliverPinParams = deriveDeliverPinParams(
      userExistsException,
      DeliverPinLocation.SIGN_IN,
      primaryPhoneNumberValue,
      primaryEmailAddressValue,
    );

    await deliverPin(deliverPinParams);

    const { deliverPinSuccess } = usePinStore.getState();

    if (deliverPinSuccess) {
      navigateWithCallback('/mail/pin-verify').then((): void => {
        clearUserSuccessState();
      });
    }
  };

  useEffect((): void => {
    if (!isReady) {
      return;
    }

    // Fire analytics event on page land signaling opt-in variant.
    sendSignUpVariantAssignedAnalytics({
      experiment_name: 'sms_opt_in_checkbox',
      variant: smsOptInCheckboxVariant,
    });
  }, [isReady, smsOptInCheckboxVariant]);

  // Because this page depends on the SMS opt-in variant to determine checkbox visibility,
  // wait until feature flags have loaded before rendering to avoid a flash of incorrect content.
  if (!isReady) {
    return null;
  }

  return (
    <div className="mail-code-activation-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header showLogo />

          <main>
            <LanderImage
              alt={t('imageAlts.vehicleSUVTopView')}
              defaultImage={vehicleSUVTopView}
            />
            <form
              data-testid={locator.pages.mailCodeActivation.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.mailCodeActivation.form.h1}>
                  {activationTitleText}
                </h1>
                <h2 data-testid={locator.pages.mailCodeActivation.form.h2}>
                  {activationSubtitleText}
                </h2>

                {deliverPinFailedMessage && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={locator.pages.mailCodeActivation.alert.error}
                      icon={false}
                      severity="error"
                    >
                      {deliverPinFailedMessage}
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('firstNameValue', firstNameValidation)}
                        error={!isEmpty(errors.firstNameValue)}
                        label={t('MailCodeActivationPage.firstName')}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.mailCodeActivation.form
                                .firstNameInput,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.firstNameValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.mailCodeActivation.form
                              .firstNameInputError
                          }
                        >
                          {toString(errors.firstNameValue.message)}
                        </span>
                      )}
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('lastNameValue', lastNameValidation)}
                        error={!isEmpty(errors.lastNameValue)}
                        label={t('MailCodeActivationPage.lastName')}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.mailCodeActivation.form
                                .lastNameInput,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.lastNameValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.mailCodeActivation.form
                              .lastNameInputError
                          }
                        >
                          {toString(errors.lastNameValue.message)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <PhoneNumberInput
                      control={control}
                      errorTestId={
                        locator.pages.mailCodeActivation.form.phoneInputError
                      }
                      inputLabelKey={'MailCodeActivationPage.phone'}
                      name="primaryPhoneNumberValue"
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.mailCodeActivation.form.phoneInput,
                        },
                      }}
                    />
                  </div>

                  <div>
                    <TextField
                      {...register(
                        'primaryEmailAddressValue',
                        emailAddressValidation,
                      )}
                      error={!isEmpty(errors.primaryEmailAddressValue)}
                      label={t('MailCodeActivationPage.email')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.mailCodeActivation.form.emailInput,
                        },
                      }}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.primaryEmailAddressValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.mailCodeActivation.form.emailInputError
                        }
                      >
                        {toString(errors.primaryEmailAddressValue.message)}
                      </span>
                    )}
                  </div>

                  {smsOptInCheckboxVariant !==
                    smsOptInCheckboxVariantDefault && (
                    <div style={{ marginTop: '1rem' }}>
                      <CssBaseline>
                        <Container
                          className={smsOptInCheckboxClassName}
                          disableGutters
                          onClick={handleSmsOptInCheckboxClicked}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                {...register('smsOptInValue')}
                                checked={watch('smsOptInValue', false)}
                                checkedIcon={<CheckedIcon />}
                                data-testid={
                                  locator.pages.mailCodeActivation.form
                                    .smsOptInCheckbox
                                }
                                icon={<UncheckedIcon />}
                              />
                            }
                            label={
                              <Trans
                                i18nKey={
                                  'MailCodeActivationPage.smsOptInAcknowledgement'
                                }
                                values={{ brand }}
                              />
                            }
                            // Allows the higher level container to handle the click event
                            style={{ pointerEvents: 'none' }}
                          />
                        </Container>
                      </CssBaseline>
                    </div>
                  )}
                </div>

                <Button
                  color="primary"
                  data-testid={
                    locator.pages.mailCodeActivation.form.submitButton
                  }
                  loading={isLoading}
                  loadingPosition="start"
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('MailCodeActivationPage.continue')}
                </Button>

                <p className="form-terms-privacy">
                  <Trans
                    components={{
                      terms: (
                        <a
                          data-testid={
                            locator.pages.mailCodeActivation.form.termsLink
                          }
                          href="https://www.thesavingsgroup.com/terms-of-use/"
                          rel="noreferrer"
                          target="_blank"
                        />
                      ),
                      privacy: (
                        <a
                          data-testid={
                            locator.pages.mailCodeActivation.form.privacyLink
                          }
                          href="https://www.thesavingsgroup.com/privacy-policy/"
                          rel="noreferrer"
                          target="_blank"
                        />
                      ),
                    }}
                    i18nKey={'MailCodeActivationPage.termsPrivacy'}
                  />
                </p>
                <a
                  className="reviews-link"
                  data-testid={
                    locator.pages.mailCodeActivation.form.reviewsLink
                  }
                  href={reviewsUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  <img alt={t('imageAlts.reviews')} src={reviewsAsset} />
                </a>
              </div>
              <footer
                data-testid={locator.pages.mailCodeActivation.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'MailCodeActivationPage';
